<template>
  <div class="list-invoices">
    <PageHeader title="Invoices" :action="{ title: 'Add new invoice', to: { name: 'CreateInvoice' } }" />

    <InvoiceStats />

    <div class="card">
      <BaseTable
      :items="invoices ?? []"
      :fields="fields"
      :is-loading="isPending"
      :error="error"
      :is-searchable="true"
      :paginate="true"
      :next-page="nextPage"
      :prev-page="prevPage"
      :page="currentPage"
      :total-records="totalRecords"
      :total-pages="totalPages"
      :number-per-page="numPerPage"
      class="mt-16"
      @on-search="(val) => search(val)"
    >
      <template #invoiceId-data="data">
        <router-link :to="{ name: 'ShowInvoice', params: { id: data.row.id } }">
          {{ data.row.invoiceId }}
        </router-link>
      </template>

      <template #status-data="data">
        <BaseBadge v-if="data.row.status === INVOICE_STATUS.OVERDUE" variant="error" class="capitalize">
          {{ data.row.status }}
        </BaseBadge>
        <BaseBadge v-else-if="data.row.status === INVOICE_STATUS.PAID" variant="success" class="capitalize">
          {{ data.row.status }}
        </BaseBadge>
        <BaseBadge v-else-if="data.row.status === INVOICE_STATUS.OUTSTANDING" variant="warning" class="capitalize">
          {{ data.row.status }}
        </BaseBadge>
      </template>

      <template #invoiceDate-data="data">
        {{ formatDate(data.row.invoiceDate) }}
      </template>

      <template #customerId-data="data">
        <RouterLink :to="{ name: 'ShowCustomer', params: { id: data.row.customerId } }">
          {{ customers?.find(customer => customer.id === data.row.customerId)?.name || data.row.customerId }}
        </RouterLink>
      </template>

      <template #projectId-data="data">
        <RouterLink :to="{ name: 'ShowProject', params: { projectId: data.row.projectId, customerId: data.row.customerId  } }">
          {{ projects?.find(project => project.id === data.row.projectId)?.name || data.row.projectId }}
        </RouterLink>
      </template>

      <template #total-data="data">
        {{ currency(data.row.total) }}
      </template>
      </BaseTable>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useHead } from '@unhead/vue'
import { useQuery } from '@tanstack/vue-query'
import InvoiceService from '@/services/InvoiceService'
import PageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { Invoice, INVOICE_STATUS } from '@/models/invoice'
import { formatDate } from '@/utils'
import currency from '@/utils/currency'
import InvoiceStats from '@/components/invoice/InvoiceStats.vue'
import ProjectService from '@/services/ProjectService'
import { inArray } from '@/utils/queryFilters'
import CustomerService from '@/services/CustomerService'
import { useSearchablePagination } from '@/composables/useSearchablePagination'

useHead({ title: 'Invoices' })

const fields = [
  { key: 'invoiceId', label: 'Invoice #', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'invoiceDate', label: 'Date', sortable: true },
  { key: 'customerId', label: 'Customer', sortable: true },
  { key: 'projectId', label: 'Project' },
  { key: 'total', label: 'Total', sortable: true },
]

const invoiceService = new InvoiceService()
const numPerPage = ref(25)

const {
  results: invoices,
  isPending,
  error,
  search,
  currentPage,
  totalPages,
  totalRecords,
  nextPage,
  prevPage,
} = useSearchablePagination<Invoice>({
  indexName: 'invoices',
  service: invoiceService,
  numPerPage: numPerPage.value,
  defaultOrderBy: 'createdAt',
  defaultOrderDir: 'desc'
})

const uniqueProjects = computed(() => Array.from(new Set(invoices.value?.map((invoice: Invoice) => invoice.projectId))))
const projectService = new ProjectService()
const { data: projects } = useQuery({
  queryKey: ['projects', uniqueProjects, currentPage],
  queryFn: () => projectService.where([inArray('id', uniqueProjects.value as string[])], [])
})

const uniqueCustomers = computed(() => Array.from(new Set(invoices.value?.map((invoice: Invoice) => invoice.customerId))))
const customerService = new CustomerService()
const { data: customers } = useQuery({
  queryKey: ['customers', uniqueCustomers, currentPage],
  queryFn: () => customerService.where([inArray('id', uniqueCustomers.value as string[])], [])
})

</script>

<style lang="scss">
.title  {
  min-height: 200px;
}
.overdue {
 color: #dc3545;
}
.notoverdue {
 color: #28a745;
}
.lastoverdue {
 color: #28a745;
}
.unpaid {
  color:  #313233;
}
.gray {
  color:  #abb2b9;
}
.vertical {
  transform: rotate(90deg);
  z-index: 1000;
  width: 200px;
  margin-top: 100px;
  margin-left: -85px;
}
.cardHeight {
  height: 350px;
}
.totalLeft {
  margin-left: -28%;
  margin-top: 23px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

</style>
